/**
 * Sponsor Dashboard Page
 * Main page for sponsor organizations to manage Medicaid enrollment programs
 * Trust Marketplace - Medicaid Enrollment Integration
 */

import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SponsorEnrollmentDashboard } from '@/components/sponsors/SponsorEnrollmentDashboard';
import { SponsorCBOPerformance } from '@/components/sponsors/SponsorCBOPerformance';
import { SponsorFinancialTracking } from '@/components/sponsors/SponsorFinancialTracking';
// Goals moved to Program Setup Wizard
// import { SponsorGoalManager } from '@/components/sponsors/SponsorGoalManager';
import { PendingApplications } from '@/components/sponsors/PendingApplications';
import ProgramManagement from '@/components/sponsors/ProgramManagement';
import CBODiscovery from '@/components/marketplace/CBODiscovery';
import ProgramSetupWizard from '@/components/sponsors/ProgramSetupWizard';
import SentInvitations from '@/components/sponsors/SentInvitations';
import MCPROIDashboard from '@/components/sponsors/MCPROIDashboard';
import SuperAdminROIDashboard from '@/components/admin/SuperAdminROIDashboard';
import { ParticipantsDashboard } from '@/components/sponsors/ParticipantsDashboard';
import SponsorPlanMembers from '@/components/sponsors/SponsorPlanMembers';
import ProgramIntelligenceCatalog from '@/components/marketplace/ProgramIntelligenceCatalog';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getWeaveinApiUrl } from '@/src/config';
import { Plus, Sparkles, LayoutDashboard, Store, BarChart3, TrendingUp, Brain, Shield, Users } from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';
export const SponsorDashboard = () => {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  // Get sponsor ID from localStorage (set by OrganizationSwitcher)
  const sponsorId = parseInt(localStorage.getItem('active_sponsor_id') || '1');
  const cboId = parseInt(localStorage.getItem('active_cbo_id') || '6');

  // Check if user is super admin
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Program selection for CBO Discovery - fetch dynamically
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [programs, setPrograms] = useState<any[]>([]);
  const [showWizard, setShowWizard] = useState(false);

  // Refresh key to trigger SentInvitations refresh when invitation is sent
  const [invitationsRefreshKey, setInvitationsRefreshKey] = useState(0);

  useEffect(() => {
    // Check if user is super admin
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const isSuper = payload.role === 'super_admin';
        setIsSuperAdmin(isSuper);
      } catch (e) {
        console.error('Failed to decode token:', e);
      }
    }

    // Fetch programs for this sponsor organization
    const fetchPrograms = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(
          getWeaveinApiUrl(`/api/programs?sponsor_id=${sponsorId}`),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-CBO-ID': cboId.toString()
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setPrograms(data);
          // Set first program as selected if available
          if (data.length > 0 && !selectedProgramId) {
            setSelectedProgramId(data[0].id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch programs:', error);
      }
    };

    fetchPrograms();
  }, [cboId]);

  // Debug removed - was causing console clutter

  if (showWizard) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ProgramSetupWizard
          sponsorId={sponsorId}
          onComplete={(programId) => {
            setSelectedProgramId(programId);
            setShowWizard(false);
            window.location.reload(); // Refresh to load new program
          }}
          onCancel={() => setShowWizard(false)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              {!tabbed && (
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('sponsordashboard.sponsor_and_program_dashboard', 'Sponsor and Program Dashboard')}</h1>
              )}
              <p className="text-gray-600 mt-1">
                {t('sponsordashboard.manage_and_monitor_programs_across', 'Manage and monitor programs across your CBO network')}
              </p>
            </div>
            <Button onClick={() => setShowWizard(true)} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              {t('sponsordashboard.create_program', 'Create Program')}
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs Navigation - Material Design Style */}
      <Tabs defaultValue="overview" className="w-full flex flex-col">
        <div className="bg-white dark:bg-gray-800 border-b-2 border-gray-100 sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-6">
            <TabsList className="flex flex-row items-center gap-0 bg-transparent border-0 p-0 h-14 w-full justify-start">
              <TabsTrigger
                value="overview"
                className="relative px-6 py-4 h-14 text-sm font-semibold text-gray-600 border-b-3 border-transparent hover:text-blue-600 hover:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:border-blue-600 data-[state=active]:bg-blue-50/50 transition-all duration-200 rounded-none flex items-center gap-2"
              >
                <LayoutDashboard className="w-4 h-4" />
                {t('sponsordashboard.overview', 'Overview')}
              </TabsTrigger>
              <TabsTrigger
                value="plan-members"
                className="relative px-6 py-4 h-14 text-sm font-semibold text-gray-600 border-b-3 border-transparent hover:text-blue-600 hover:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:border-blue-600 data-[state=active]:bg-blue-50/50 transition-all duration-200 rounded-none flex items-center gap-2"
              >
                <Users className="w-4 h-4" />
                {t('sponsordashboard.plan_members', 'Plan Members')}
              </TabsTrigger>
              <TabsTrigger
                value="marketplace"
                className="relative px-6 py-4 h-14 text-sm font-semibold text-gray-600 border-b-3 border-transparent hover:text-blue-600 hover:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:border-blue-600 data-[state=active]:bg-blue-50/50 transition-all duration-200 rounded-none flex items-center gap-2"
              >
                <Store className="w-4 h-4" />
                {t('sponsordashboard.marketplace', 'Marketplace')}
              </TabsTrigger>
              <TabsTrigger
                value="program-performance"
                className="relative px-6 py-4 h-14 text-sm font-semibold text-gray-600 border-b-3 border-transparent hover:text-blue-600 hover:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:border-blue-600 data-[state=active]:bg-blue-50/50 transition-all duration-200 rounded-none flex items-center gap-2"
              >
                <BarChart3 className="w-4 h-4" />
                {t('sponsordashboard.program_performance', 'Program Performance')}
              </TabsTrigger>
              <TabsTrigger
                value="roi"
                className="relative px-6 py-4 h-14 text-sm font-semibold text-gray-600 border-b-3 border-transparent hover:text-blue-600 hover:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:border-blue-600 data-[state=active]:bg-blue-50/50 transition-all duration-200 rounded-none flex items-center gap-2"
              >
                <TrendingUp className="w-4 h-4" />
                {t('sponsordashboard.roi_dashboard', 'ROI Dashboard')}
              </TabsTrigger>
              <TabsTrigger
                value="intelligence"
                className="relative px-6 py-4 h-14 text-sm font-semibold text-gray-600 border-b-3 border-transparent hover:text-purple-600 hover:bg-purple-50 data-[state=active]:text-purple-600 data-[state=active]:border-purple-600 data-[state=active]:bg-purple-50/50 transition-all duration-200 rounded-none flex items-center gap-2"
              >
                <Brain className="w-4 h-4" />
                {t('sponsordashboard.program_intelligence', 'Program Intelligence')}
              </TabsTrigger>
              {isSuperAdmin && (
                <TabsTrigger
                  value="platform-roi"
                  className="relative px-6 py-4 h-14 text-sm font-semibold text-gray-600 border-b-3 border-transparent hover:text-blue-600 hover:bg-blue-50 data-[state=active]:text-blue-600 data-[state=active]:border-blue-600 data-[state=active]:bg-blue-50/50 transition-all duration-200 rounded-none flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  {t('sponsordashboard.platform_roi', 'Platform ROI')}
                </TabsTrigger>
              )}
            </TabsList>
          </div>
        </div>

        {/* Tab Content - Full Width with consistent padding */}
        <div className="max-w-full">
          <div className="max-w-7xl mx-auto px-6 py-8">
          <TabsContent value="plan-members" className="mt-0">
            <SponsorPlanMembers sponsorId={sponsorId} />
          </TabsContent>

          <TabsContent value="overview" className="mt-0 space-y-8">
            {/* Program Selector for Overview */}
            {programs.length > 1 && (
              <Card className="p-4">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('sponsordashboard.select_program', 'Select Program:')}
                  </label>
                  <select
                    value={selectedProgramId || ''}
                    onChange={(e) => setSelectedProgramId(parseInt(e.target.value))}
                    className="flex-1 max-w-md px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {programs.map((program) => (
                      <option key={program.id} value={program.id}>
                        {program.name} ({program.program_type})
                      </option>
                    ))}
                  </select>
                </div>
              </Card>
            )}

            {/* Program Management Section */}
            <ProgramManagement
              sponsorId={sponsorId}
              cboId={cboId}
            />

            {/* Enrollment Metrics Section */}
            <SponsorEnrollmentDashboard
              sponsorId={sponsorId}
              programId={selectedProgramId || (programs.length > 0 ? programs[0].id : undefined)}
            />
          </TabsContent>

          <TabsContent value="marketplace" className="mt-0">
            {programs.length > 0 ? (
              <div className="space-y-6">
                {/* Program Selector */}
                <Card className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('sponsordashboard.select_program_1', 'Select Program')}
                      </label>
                      <select
                        value={selectedProgramId || ''}
                        onChange={(e) => setSelectedProgramId(parseInt(e.target.value))}
                        className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">{t('sponsordashboard.select_a_program', 'Select a program...')}</option>
                        {programs.map((program) => (
                          <option key={program.id} value={program.id}>
                            {program.name} ({program.program_type})
                          </option>
                        ))}
                      </select>
                    </div>
                    <Button onClick={() => setShowWizard(true)} className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      {t('sponsordashboard.new_program', 'New Program')}
                    </Button>
                  </div>
                </Card>

                {/* Pending Applications Section */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('sponsordashboard.pending_applications', 'Pending Applications')}</h3>
                  <PendingApplications sponsorId={sponsorId} />
                </Card>

                {/* CBO Discovery for Selected Program */}
                {selectedProgramId ? (
                  <>
                    <CBODiscovery
                      sponsorId={sponsorId}
                      programId={selectedProgramId}
                      onInviteSent={() => setInvitationsRefreshKey(k => k + 1)}
                    />

                    {/* Sent Invitations for Selected Program */}
                    <Card className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('sponsordashboard.sent_invitations', 'Sent Invitations')}</h3>
                      <SentInvitations
                        sponsorId={sponsorId}
                        programId={selectedProgramId}
                        refreshKey={invitationsRefreshKey}
                        onInvitationWithdrawn={() => {
                          // Could trigger a refresh of CBODiscovery if needed
                        }}
                      />
                    </Card>
                  </>
                ) : (
                  <Card className="p-12 text-center">
                    <Sparkles className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {t('sponsordashboard.select_a_program_1', 'Select a Program')}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      {t('sponsordashboard.choose_a_program_above_to', 'Choose a program above to discover and invite compatible CBOs')}
                    </p>
                  </Card>
                )}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <Sparkles className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('sponsordashboard.no_programs_yet', 'No Programs Yet')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t('sponsordashboard.create_your_first_program_to', 'Create your first program to discover and invite CBOs to your network')}
                </p>
                <Button onClick={() => setShowWizard(true)} size="lg" className="flex items-center gap-2 mx-auto">
                  <Plus className="w-5 h-5" />
                  {t('sponsordashboard.create_your_first_program', 'Create Your First Program')}
                </Button>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="program-performance" className="mt-0 space-y-8">
            {/* Program Selector */}
            {programs.length > 1 && (
              <Card className="p-4">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('sponsordashboard.select_program_2', 'Select Program:')}
                  </label>
                  <select
                    value={selectedProgramId || ''}
                    onChange={(e) => setSelectedProgramId(parseInt(e.target.value))}
                    className="flex-1 max-w-md px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {programs.map((program) => (
                      <option key={program.id} value={program.id}>
                        {program.name} ({program.program_type})
                      </option>
                    ))}
                  </select>
                </div>
              </Card>
            )}

            {/* Participants Section */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('sponsordashboard.participants', 'Participants')}</h2>
              <ParticipantsDashboard
                sponsorId={sponsorId}
                programId={selectedProgramId}
              />
            </div>

            {/* Performance Metrics Section */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('sponsordashboard.performance_metrics', 'Performance Metrics')}</h2>
              <SponsorCBOPerformance sponsorId={sponsorId} />
            </div>

            {/* Financial Tracking Section */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('sponsordashboard.financial_tracking', 'Financial Tracking')}</h2>
              <SponsorFinancialTracking sponsorId={sponsorId} />
            </div>
          </TabsContent>

          <TabsContent value="roi" className="mt-0 space-y-6">
            {/* Program Selector for ROI */}
            {programs.length > 0 && (
              <Card className="p-4">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('sponsordashboard.select_program_3', 'Select Program:')}
                  </label>
                  <select
                    value={selectedProgramId || ''}
                    onChange={(e) => setSelectedProgramId(parseInt(e.target.value))}
                    className="flex-1 max-w-md px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {programs.map((program) => (
                      <option key={program.id} value={program.id}>
                        {program.name} ({program.program_type})
                      </option>
                    ))}
                  </select>
                </div>
              </Card>
            )}

            {selectedProgramId ? (
              <MCPROIDashboard programId={selectedProgramId} />
            ) : programs.length > 0 ? (
              <MCPROIDashboard programId={programs[0].id} />
            ) : (
              <Card className="p-12 text-center">
                <Sparkles className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('sponsordashboard.no_programs_found', 'No Programs Found')}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t('sponsordashboard.create_a_program_to_view', 'Create a program to view ROI metrics and analytics')}
                </p>
                <Button onClick={() => setShowWizard(true)} size="lg" className="flex items-center gap-2 mx-auto">
                  <Plus className="w-5 h-5" />
                  {t('sponsordashboard.create_your_first_program_1', 'Create Your First Program')}
                </Button>
              </Card>
            )}
          </TabsContent>

          {isSuperAdmin && (
            <TabsContent value="platform-roi" className="mt-0">
              {/* SuperAdminROIDashboard has its own internal program selector */}
              {programs.length > 0 ? (
                <SuperAdminROIDashboard programId={selectedProgramId || programs[0].id} />
              ) : (
                <Card className="p-12 text-center">
                  <Sparkles className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {t('sponsordashboard.no_programs_found_1', 'No Programs Found')}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {t('sponsordashboard.create_a_program_to_view_1', 'Create a program to view platform ROI metrics')}
                  </p>
                  <Button onClick={() => setShowWizard(true)} size="lg" className="flex items-center gap-2 mx-auto">
                    <Plus className="w-5 h-5" />
                    {t('sponsordashboard.create_your_first_program_2', 'Create Your First Program')}
                  </Button>
                </Card>
              )}
            </TabsContent>
          )}

          <TabsContent value="intelligence" className="mt-0 space-y-8">
            {/* Program Intelligence - Discovered Programs Catalog */}
            <ProgramIntelligenceCatalog cboId={cboId} />
          </TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  );
};

export default SponsorDashboard;
